import type { Order } from '../../types/order';
import { hasRevenueForMarginPercent } from './orderFinanceDisplay';
import { getOrderMarginTone, OrderMarginTone } from './orderMarginPresentation';

/**
 * Order-level margin facts from the backend cost model
 * (`orderCostBreakdownService`: FIFO COGS + transport and other order costs).
 *
 * `totalCost` / `margin` / `marginPercent` are `null` when the cost is unknown:
 * `responseFilter.filterOrderData` nulls them while the warehouse cost is still
 * pending. Unknown is not zero.
 */
export type OrderMarginFacts = Pick<
  Order,
  'revenue' | 'totalCost' | 'margin' | 'marginPercent'
> & Partial<Pick<Order, 'warehouseCostPending'>>;

/**
 * THE rule of whether an order's cost is known (owner decision 2026-10-05),
 * shared by the ZK card, the dashboard and the profitability analysis:
 *
 * - `UNKNOWN`: no cost at all (`margin`/`totalCost` null) or the plan is
 *   incomplete (`warehouseCostPending`) and nothing of it is known yet
 *   (`totalCost` ≤ 0) — the stored margin equals the revenue, so it is shown
 *   as „Marża: koszt nieznany”, never as a green amount or „100%”.
 * - `PARTIAL`: incomplete plan with a known part — the known-part amount may
 *   be shown with an explicit „niepełny koszt”, never as a success and never
 *   with a percent.
 * - `KNOWN`: every cost known.
 *
 * The goods without cost are priced at the product's average purchase cost
 * (WP5); a cost is unknown only when that average cannot be used.
 */
export type OrderMarginCostState = 'KNOWN' | 'PARTIAL' | 'UNKNOWN';

export function getOrderMarginCostState(
  facts: Pick<OrderMarginFacts, 'totalCost' | 'margin' | 'warehouseCostPending'>,
): OrderMarginCostState {
  if (facts.margin == null || facts.totalCost == null) return 'UNKNOWN';
  if (facts.warehouseCostPending === true) return Number(facts.totalCost) > 0 ? 'PARTIAL' : 'UNKNOWN';
  return 'KNOWN';
}

/** A margin percent is shown only for a fully known cost. */
export const canShowOrderMarginPercent = (state: OrderMarginCostState) => state === 'KNOWN';

/**
 * Why an order margin percent cannot be shown. `NO_REVENUE` and `NO_COST` are
 * both "undefined percent", never a fabricated `0%` or `100%` — the same rule
 * the profitability report applies through `displayMarginPercent`/`getMarginBucket`.
 */
export type OrderMarginPercentState = 'KNOWN' | 'NO_REVENUE' | 'NO_COST' | 'COST_INCOMPLETE';

export const ORDER_MARGIN_PERCENT_UNAVAILABLE_KEYS = {
  NO_REVENUE: 'orders:orderDetail.marginPercentUnavailable',
  NO_COST: 'orders:orderDetail.marginPercentNoCost',
  COST_INCOMPLETE: 'orders:orderDetail.marginPercentCostIncomplete',
} as const;

/** Money is stored in PLN (migration 054); a missing amount displays as zero. */
export const toOrderMarginAmount = (value: number | null | undefined): number => Number(value ?? 0);

export function getOrderMarginPercentState(
  facts: Pick<OrderMarginFacts, 'revenue' | 'totalCost' | 'warehouseCostPending'>,
): OrderMarginPercentState {
  if (!hasRevenueForMarginPercent(toOrderMarginAmount(facts.revenue))) return 'NO_REVENUE';
  if (toOrderMarginAmount(facts.totalCost) <= 0) return 'NO_COST';
  if (facts.warehouseCostPending === true) return 'COST_INCOMPLETE';
  return 'KNOWN';
}

export interface OrderMarginSummary {
  revenue: number;
  totalCost: number;
  margin: number;
  tone: OrderMarginTone;
  costState: OrderMarginCostState;
  /** `orders:orderDetail.<statusKey>` describing the result. */
  statusKey: 'marginResultPositive' | 'marginResultNegative' | 'marginResultWatch';
  percentState: OrderMarginPercentState;
  /** Percent to format, or `null` when `percentUnavailableKey` must be shown instead. */
  percent: number | null;
  percentUnavailableKey: string | null;
}

/** Single home for how an order's cost-model margin is presented (finances tab). */
export function getOrderMarginSummary(facts: OrderMarginFacts): OrderMarginSummary {
  const revenue = toOrderMarginAmount(facts.revenue);
  const totalCost = toOrderMarginAmount(facts.totalCost);
  const margin = toOrderMarginAmount(facts.margin);
  const marginPercent = toOrderMarginAmount(facts.marginPercent);
  const tone = getOrderMarginTone(margin, marginPercent);
  const percentState = getOrderMarginPercentState(facts);

  return {
    revenue,
    totalCost,
    margin,
    tone,
    costState: getOrderMarginCostState(facts),
    statusKey: tone === 'loss'
      ? 'marginResultNegative'
      : tone === 'watch'
        ? 'marginResultWatch'
        : 'marginResultPositive',
    percentState,
    percent: percentState === 'KNOWN' ? marginPercent : null,
    percentUnavailableKey: percentState === 'KNOWN'
      ? null
      : ORDER_MARGIN_PERCENT_UNAVAILABLE_KEYS[percentState],
  };
}
